// ReminderPromptSheet — redesign Phase 8a, "first step done + reminder
// prompt". Built to docs/designs/OnbStepDone.dc.html.
//
// Shown the first time someone completes a devotional step (or opens the
// sermon recap, if there's no devotional), and only if they haven't been
// asked about reminders before. Two states: asking, then a confirmation
// once "Remind me at…" is tapped.
//
// Push-permission note: this sheet does not call any native bridge
// permission API — see docs/REDESIGN-SPEC's onboarding plan Context for why
// (Median's own dashboard settings control when the OS dialog actually
// fires, which isn't something this code can see or change). The reminder
// preference is saved to `profiles` regardless of the OS outcome.
//
// Props:
//   firstName, churchName, otherCount — for the body copy
//   onRemind(time)       — "Remind me at [time]" tapped; caller persists
//   onNotNow()            — "Not now" tapped; caller persists + closes
//   onCarryOn()            — "Carry on: Reflect" tapped (post-accept)
//   onBackToWeek()         — "Back to This Week" tapped (post-accept)

import { useState } from "react";

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const TIMES = ["6:30am", "7:00am", "12:30pm", "9:00pm"];

export default function ReminderPromptSheet({ firstName, churchName, otherCount = 0, onRemind, onNotNow, onCarryOn, onBackToWeek }) {
  const [sel, setSel] = useState(1); // default 7:00am
  const [accepted, setAccepted] = useState(false);
  const chosen = TIMES[sel];

  function accept() {
    setAccepted(true);
    onRemind?.(chosen);
  }

  const body = otherCount >= 2
    ? `${otherCount} others from ${churchName} read this today. Want a gentle nudge tomorrow?`
    : "Want a gentle nudge tomorrow?";

  return (
    <div style={S.backdrop}>
      <div style={S.sheet}>
        <div style={S.handle}/>
        <svg style={S.sun} viewBox="0 0 100 80" aria-hidden="true">
          <circle cx="50" cy="52" r="20" fill="#E8C87A"/>
          <g stroke="#E8C87A" strokeWidth="4" strokeLinecap="round">
            <line x1="50" y1="18" x2="50" y2="8"/>
            <line x1="74" y1="28" x2="82" y2="20"/>
            <line x1="26" y1="28" x2="18" y2="20"/>
          </g>
          <path d="M50 48 L50 78 L20 78 Q16 78 16 72 L16 54 Q16 48 22 48 Z" fill="#1F1B16"/>
          <path d="M50 48 L50 78 L80 78 Q84 78 84 72 L84 54 Q84 48 78 48 Z" fill="#3A332C"/>
        </svg>

        {!accepted ? (
          <>
            <div style={S.heading}>First step done, {firstName}</div>
            <div style={S.body}>{body}</div>
            <div style={S.chips}>
              {TIMES.map((t, i) => (
                <button key={t} onClick={() => setSel(i)} style={{ ...S.chip, ...(i === sel ? S.chipSel : null) }}>{t}</button>
              ))}
            </div>
            <button style={S.primaryBtn} onClick={accept}>Remind me at {chosen}</button>
            <button style={S.secondaryBtn} onClick={onNotNow}>Not now</button>
          </>
        ) : (
          <>
            <div style={S.heading}>See you tomorrow at {chosen}</div>
            <div style={S.body}>(Your phone will now ask permission to send notifications.)</div>
            <button style={S.goldBtn} onClick={onCarryOn}>Carry on: Reflect</button>
            <button style={S.secondaryBtn} onClick={onBackToWeek}>Back to This Week</button>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: "fixed", inset: 0, zIndex: 900, background: "rgba(31,27,22,0.35)", display: "flex", flexDirection: "column", justifyContent: "flex-end", fontFamily: SANS },
  sheet: { background: "#F6F2EC", borderRadius: "32px 32px 0 0", padding: "14px 24px calc(env(safe-area-inset-bottom) + 34px)", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 },
  handle: { width: 40, height: 5, borderRadius: 3, background: "#D6CEC2" },
  sun: { width: 92, height: 74, flexShrink: 0 },
  heading: { fontSize: 24, fontWeight: 800, letterSpacing: "-0.01em", color: "#1F1B16", textAlign: "center" },
  body: { fontSize: 14.5, color: "#6B6258", lineHeight: 1.5, textAlign: "center" },
  chips: { display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" },
  chip: { minHeight: 44, padding: "0 12px", borderRadius: 999, fontFamily: SANS, fontSize: 13.5, fontWeight: 700, cursor: "pointer", color: "#1F1B16", border: "2px solid transparent", background: "#FFFFFF" },
  chipSel: { border: "2px solid #1F1B16", background: "#F5E4B8" },
  primaryBtn: { width: "100%", minHeight: 54, background: "#1F1B16", color: "#FFFFFF", border: "none", borderRadius: 999, fontFamily: SANS, fontWeight: 700, fontSize: 16, cursor: "pointer" },
  goldBtn: { width: "100%", minHeight: 54, background: "#E8C87A", color: "#1F1B16", border: "none", borderRadius: 999, fontFamily: SANS, fontWeight: 700, fontSize: 16, cursor: "pointer" },
  secondaryBtn: { background: "transparent", border: "none", color: "#7A5A12", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: SANS },
};
